// Talks to the planner worker; falls back to the main thread where module workers are unavailable (tests, very old
// browsers). The data is sent once per data generation (a structured clone), each plan request is small.

import type { GeoBounds, TrailDataset, TrailRoute, WeatherForecast } from '../core/models';
import type { SleepTonightRequestInit, SleepTonightResult } from '../engine';
import { handlePlannerMessage, type PlannerReply, type PlannerRequest } from './plannerCore';

export interface PlanInput {
  route: TrailRoute;
  personalFactor: number;
  request: SleepTonightRequestInit;
  forecast?: WeatherForecast;
  offlineBounds: GeoBounds[];
}

export class PlannerClient {
  private worker: Worker | undefined;
  private nextID = 1;
  private generation = 0;
  private dataset: TrailDataset | undefined;
  private offlineTiles: string[] = [];
  private readonly pending = new Map<number, { resolve: (r: SleepTonightResult) => void; reject: (e: Error) => void; input: PlanInput }>();

  constructor() {
    if (typeof Worker !== 'undefined') {
      try {
        this.worker = new Worker(new URL('./planner.worker.ts', import.meta.url), { type: 'module' });
        this.worker.onmessage = (event: MessageEvent<PlannerReply>) => this.receive(event.data);
        this.worker.onerror = () => {
          // The worker could not start: plan on the main thread from now on.
          this.worker?.terminate();
          this.worker = undefined;
          if (this.dataset) this.send({ type: 'data', generation: this.generation, dataset: this.dataset });
          for (const [id, entry] of this.pending) this.dispatch(id, entry.input);
        };
      } catch {
        this.worker = undefined;
      }
    }
  }

  /** New merged dataset (after every reload of the app's data). */
  setData(dataset: TrailDataset): void {
    this.generation += 1;
    this.dataset = dataset;
    this.send({ type: 'data', generation: this.generation, dataset });
  }

  /** Downloaded map coverage ("x/y" tile keys at zoom 14), attached to every plan request. */
  setOfflineTiles(keys: string[]): void {
    this.offlineTiles = keys;
  }

  plan(input: PlanInput): Promise<SleepTonightResult> {
    const id = this.nextID++;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject, input });
      this.dispatch(id, input);
    });
  }

  private dispatch(id: number, input: PlanInput) {
    this.send({ type: 'plan', id, generation: this.generation, offlineTiles: this.offlineTiles, ...input });
  }

  private send(message: PlannerRequest) {
    if (this.worker) {
      this.worker.postMessage(message);
    } else {
      // Main thread: yield first so the UI can show its progress state.
      setTimeout(() => {
        const reply = handlePlannerMessage(message);
        if (reply) this.receive(reply);
      }, 0);
    }
  }

  private receive(reply: PlannerReply) {
    const entry = this.pending.get(reply.id);
    if (!entry) return;
    if (reply.type === 'error' && reply.staleData) {
      // The data changed while this request was in flight: ask again with the current generation.
      this.dispatch(reply.id, entry.input);
      return;
    }
    this.pending.delete(reply.id);
    if (reply.type === 'result') entry.resolve(reply.result);
    else entry.reject(new Error(reply.message));
  }
}
